<template>
  <div class="hierarchy-page" v-if="ds != null">
    <div class="hierarchy-toolbar">
      <button class="toolbar-btn" @click="expandAll">Expand All</button>
      <button class="toolbar-btn" @click="collapseAll">Collapse All</button>
      <button :class="['toolbar-btn', { 'toolbar-btn--active': horizontal }]" @click="horizontal = !horizontal">
        {{ horizontal ? '↔ Horizontal' : '↕ Vertical' }}
      </button>
    </div>
    <div class="orgchart-scroll-wrapper">
      <div class="orgchart-root">
        <org-node
          :node="ds"
          :collapsed-map="collapsedMap"
          :horizontal="horizontal"
          @toggle="toggleNode"
          @navigate="navigateToUser"
        />
      </div>
    </div>
  </div>
  <div v-else class="spinner-container">
    <div class="loading-spinner"></div>
    <div class="loading-text">Loading hierarchy...</div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import OrgNode from './OrgNode.vue';

export default {
  name: 'HierarchyView2',
  components: { OrgNode },
  data() {
    return {
      ds: null,
      collapsedMap: {},
      horizontal: true,
    };
  },
  created() {
    this.loadData();
  },
  methods: {
    ...mapActions('businessesUnits', ['getHierarchy']),
    navigateToUser(id) {
      this.$router.push('/configurations/users?userId=' + id + '&tab=0');
    },
    loadData() {
      this.getHierarchy().then((response) => {
        this.ds = response;
      });
    },
    toggleNode(key) {
      this.$set(this.collapsedMap, key, !this.collapsedMap[key]);
    },
    collectKeys(node, result) {
      const key = node.id + '_' + node.title;
      result.push(key);
      if (node.children) {
        node.children.forEach((c) => this.collectKeys(c, result));
      }
    },
    expandAll() {
      this.collapsedMap = {};
    },
    collapseAll() {
      const keys = [];
      this.collectKeys(this.ds, keys);
      const map = {};
      keys.forEach((k) => (map[k] = true));
      this.collapsedMap = map;
    },
  },
};
</script>

<style>
.hierarchy-page {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #f4f6f9;
}

.hierarchy-toolbar {
  display: flex;
  gap: 8px;
  padding: 12px 20px;
  background: #fff;
  border-bottom: 1px solid #e2e8f0;
  flex-shrink: 0;
}

.dark-layout .hierarchy-page {
  background: #1f2937;
}
.dark-layout .hierarchy-toolbar{
  background: #1f2937;
  border-bottom: 1px solid #374151;
}

.toolbar-btn {
  padding: 6px 16px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  background: #fff;
  color: #475569;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
}

.toolbar-btn:hover {
  background: #f1f5f9;
  border-color: #94a3b8;
}

.toolbar-btn--active {
  background: #eef2ff;
  border-color: #6366f1;
  color: #4f46e5;
}

.orgchart-scroll-wrapper {
  flex: 1;
  overflow: auto;
  padding: 24px;
}

.orgchart-root {
  min-width: max-content;
}

/* Tree layout */
.org-tree-node {
  display: flex;
  flex-direction: column;
}

.org-tree-row {
  display: flex;
  align-items: center;
}

/* ===== VERTICAL MODE (default) ===== */
.org-tree-children {
  padding-left: 32px;
  position: relative;
}

.org-tree-child {
  display: flex;
  align-items: stretch;
  position: relative;
}

.org-tree-connector {
  width: 24px;
  flex-shrink: 0;
  position: relative;
}

/* Vertical trunk connecting siblings */
.org-tree-connector::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  border-left: 2px solid #cbd5e1;
}

/* Horizontal branch to the child card */
.org-tree-connector .connector-line {
  position: absolute;
  top: 26px;
  left: 0;
  width: 100%;
  height: 0;
  border-top: 2px solid #cbd5e1;
}

/* First child: trunk starts at card center */
.org-tree-child:first-child > .org-tree-connector::before {
  top: 26px;
}

/* Last child: trunk ends at card center */
.org-tree-child:last-child > .org-tree-connector::before {
  bottom: auto;
  height: 26px;
}

/* Only child: no trunk needed, just the branch */
.org-tree-child:only-child > .org-tree-connector::before {
  display: none;
}

/* ===== HORIZONTAL MODE ===== */
.org-tree-node--horizontal {
  flex-direction: row;
  align-items: flex-start;
}

.org-tree-node--horizontal > .org-tree-row {
  flex-shrink: 0;
}

.org-tree-children--horizontal {
  padding-left: 0;
  display: flex;
  flex-direction: column;
}

/* In horizontal mode, connector sits between parent and child horizontally */
.org-tree-connector--horizontal {
  width: 28px;
  align-self: stretch;
}

/* Vertical trunk connecting siblings */
.org-tree-connector--horizontal::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  border-left: 2px solid #cbd5e1;
}

/* Horizontal branch to child card */
.org-tree-connector--horizontal .connector-line {
  position: absolute;
  top: 26px;
  left: 0;
  width: 100%;
  height: 0;
  border-top: 2px solid #cbd5e1;
  background: none;
  border-radius: 0;
}

/* First child: trunk starts at card center */
.org-tree-child:first-child > .org-tree-connector--horizontal::before {
  top: 26px;
}

/* Last child: trunk ends at card center */
.org-tree-child:last-child > .org-tree-connector--horizontal::before {
  bottom: auto;
  height: 26px;
}

/* Only child: no trunk, just horizontal branch */
.org-tree-child:only-child > .org-tree-connector--horizontal::before {
  display: none;
}

/* Toggle button position in horizontal mode */
.org-tree-node--horizontal > .org-tree-row .org-card__toggle {
  right: auto;
  bottom: -12px;
  top: auto;
  left: 50%;
  transform: translateX(-50%);
}

/* Cards */
.org-card {
  position: relative;
  display: inline-flex;
  flex-direction: column;
  min-width: 180px;
  max-width: 260px;
  margin: 4px 0;
  border-radius: 10px;
  background: #fff;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  transition: box-shadow 0.2s, transform 0.15s;
  overflow: visible;
}

.dark-layout .org-card{
  background: #374151;

}

.org-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transform: translateY(-1px);
}

.org-card__header {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px 4px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.org-card__icon {
  font-size: 14px;
}

.org-card__title {
  flex: 1;
}

.org-card__badge {
  background: rgba(0, 0, 0, 0.08);
  color: inherit;
  font-size: 10px;
  font-weight: 700;
  padding: 1px 6px;
  border-radius: 10px;
  min-width: 18px;
  text-align: center;
}

.org-card__body {
  padding: 2px 12px 10px;
}

.org-card__name {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  word-break: break-word;
}

.dark-layout .org-card__name {
  color: #ccc;
}

.org-card__name--link {
  color: #2563eb;
  cursor: pointer;
  text-decoration: none;
}

.dark-layout .org-card__name--link {
  color: #2563eb;
}

.org-card__name--link:hover {
  text-decoration: underline;
  color: #1d4ed8;
}

/* Toggle button */
.org-card__toggle {
  position: absolute;
  right: -12px;
  top: 50%;
  transform: translateY(-50%);
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 2px solid #cbd5e1;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 1;
  transition: all 0.15s;
  padding: 0;
}

.org-card__toggle:hover {
  border-color: #94a3b8;
  background: #f8fafc;
}

.toggle-arrow {
  display: block;
  width: 0;
  height: 0;
  border-style: solid;
}

.toggle-arrow--down {
  border-width: 5px 4px 0 4px;
  border-color: #64748b transparent transparent transparent;
}

.toggle-arrow--right {
  border-width: 4px 0 4px 5px;
  border-color: transparent transparent transparent #64748b;
}

/* Color themes per node type */
.org-card--platform {
  border-color: #6366f1;
  border-width: 2px;
}
.org-card--platform .org-card__header {
  color: #4f46e5;
}
.org-card--platform .org-card__badge {
  background: rgba(99, 102, 241, 0.12);
}

.org-card--organization {
  border-color: #0ea5e9;
  border-width: 2px;
}
.org-card--organization .org-card__header {
  color: #0284c7;
}
.org-card--organization .org-card__badge {
  background: rgba(14, 165, 233, 0.12);
}

.org-card--branch {
  border-color: #f59e0b;
  border-left-width: 4px;
}
.org-card--branch .org-card__header {
  color: #d97706;
}
.org-card--branch .org-card__badge {
  background: rgba(245, 158, 11, 0.12);
}

.org-card--admin {
  border-color: #c9c600;
  border-left-width: 4px;
}
.org-card--admin .org-card__header {
  color: #c9c600;
}
.org-card--admin .org-card__badge {
  background: rgba(16, 185, 129, 0.12);
}

.org-card--manager {
  border-color: #10b981;
  border-left-width: 4px;
}
.org-card--manager .org-card__header {
  color: #059669;
}
.org-card--manager .org-card__badge {
  background: rgba(16, 185, 129, 0.12);
}

.org-card--agent {
  border-color: #e2e8f0;
  min-width: 150px;
}
.org-card--agent .org-card__header {
  color: #64748b;
}

.org-card--agent .org-card__body {
  padding-bottom: 8px;
}

.org-card--agent .org-card__name {
  font-size: 13px;
  font-weight: 500;
}

/* Loading */
.spinner-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100vh;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #e2e8f0;
  border-top-color: #6366f1;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.loading-text {
  margin-top: 16px;
  font-size: 14px;
  color: #64748b;
}
</style>
